import { useQuery } from "@tanstack/react-query"
import { Channel, DefaultGenerics, StreamChat } from "stream-chat"

import { StreamError } from "@bullseye/types"

import { useUserContext } from "../providers/UserAuthProvider/UserProvider"

export const useAllChannels = (
  client: StreamChat<DefaultGenerics> | undefined,
) => {
  const { user } = useUserContext()

  const res = useQuery(
    ["allChannels"],
    async () => {
      let allChannels: Channel<DefaultGenerics>[] = []

      const limit = 100 // The number of channels to fetch per API call
      let offset = 0
      let hasMoreChannels = true

      while (hasMoreChannels && !!client) {
        const response = await client.queryChannels(
          {
            members: { $in: [user.stream_id] },
            type: "messaging",
            // has_unread: true, // Use this if the channel lists are factored out, for better performance.
          },
          { last_message_at: -1 },
          {
            limit,
            offset,
            state: true, // We need the channel state for unread counts
            message_limit: 0, // Save space by not fetching actual messags
          },
        )

        allChannels = [...allChannels, ...response]

        // If the API returns fewer channels than the limit, we've reached the end
        if (response.length < limit) {
          hasMoreChannels = false
        } else {
          // Otherwise, prepare to fetch the next page
          offset += limit
        }
      }

      return allChannels
    },
    {
      enabled: !!client,
      staleTime: 5 * 60 * 1000, // 5 minutes - avoid refetching on every page navigation
      refetchOnWindowFocus: false,
    },
  )

  return {
    ...res,
    channels: res.data,
    error: res.error as StreamError,
  }
}
